import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import Button from '../Button/Button';
import { useAnimatedVisibility } from '../../../hooks/useAnimatedVisibility';
import styles from './Modal.module.css';

export type ModalVariant = 'default' | 'confirm' | 'alert';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  variant?: ModalVariant;
  children?: React.ReactNode;
  // For confirm variant
  message?: string;
  messageClassName?: string;
  confirmText?: string;
  cancelText?: string;
  onConfirm?: () => void;
  onCancel?: () => void;
  confirmVariant?: 'primary' | 'secondary';
  isDanger?: boolean;
  disabled?: boolean;
  // For alert variant
  alertText?: string;
  onAlertAction?: () => void;
  // Custom content for default variant
  className?: string;
}

/**
 * Modal component with variants for different use cases.
 */
const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  title,
  variant = 'default',
  children,
  message,
  messageClassName = '',
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  confirmVariant = 'primary',
  isDanger = false,
  disabled = false,
  alertText,
  onAlertAction,
  className = ''
}) => {
  const { shouldRender, isClosing, onAnimationEnd } = useAnimatedVisibility({
    isOpen,
    closeDuration: 300,
  });

  // Handle ESC key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    // Prevent body scroll when modal is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!shouldRender) return null;

  const handleOverlayClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const renderContent = () => {
    switch (variant) {
      case 'confirm':
        return (
          <>
            {message && <p className={`${styles.modalMessage} ${messageClassName}`}>{message}</p>}
            <div className={styles.modalActions}>
              <Button
                variant="secondary"
                size="medium"
                onClick={onCancel || onClose}
                className={styles.modalActionButton}
              >
                {cancelText || 'Cancel'}
              </Button>
              <Button
                variant={confirmVariant}
                size="medium"
                onClick={onConfirm}
                disabled={disabled}
                className={`${styles.modalActionButton} ${isDanger ? styles.dangerButton : ''}`}
              >
                {confirmText || 'Confirm'}
              </Button>
            </div>
          </>
        );

      case 'alert':
        return (
          <>
            {message && <p className={`${styles.modalMessage} ${messageClassName}`}>{message}</p>}
            <div className={styles.modalActions}>
              <Button
                variant="primary"
                size="medium"
                onClick={onAlertAction || onClose}
                className={styles.modalActionButton}
              >
                {alertText || 'OK'}
              </Button>
            </div>
          </>
        );

      case 'default':
      default:
        return children;
    }
  };

  return createPortal(
    <div
      className={`${styles.modalOverlay} ${isClosing ? styles.modalOverlayClosing : ''}`}
      onClick={handleOverlayClick}
      onAnimationEnd={onAnimationEnd}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        className={`${styles.modalContent} ${isClosing ? styles.modalContentClosing : ''} ${className}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <div className={styles.modalHeaderSpacer}></div>
          <h3 className={styles.modalTitle} id="modal-title">{title}</h3>
          <button
            className={styles.modalCloseButton}
            onClick={onClose}
            aria-label="Close"
            type="button"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </div>
        {renderContent()}
      </div>
    </div>,
    document.body
  );
};

export default Modal;

